import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortableTable } from './hooks/useSortableTable';
import { apiFetch } from './utils/api';

function SiteSalesOrdersTab({ siteId, siteName, customerId, customerName }) {
  const navigate = useNavigate();
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Sorting
  const { items: sortedSOs, requestSort, getSortIndicator } = useSortableTable(salesOrders, { key: 'order_date', direction: 'desc' });

  useEffect(() => {
    fetchSalesOrders();
  }, [siteId]);

  const fetchSalesOrders = async () => {
    try {
      const res = await apiFetch('/api/sales-orders');
      const allSOs = await res.json();
      
      // Filter sales orders for this specific site
      const siteSOs = allSOs.filter(so => so.site_id === parseInt(siteId));
      setSalesOrders(siteSOs);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching sales orders:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-yellow-100 text-yellow-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Sales Orders for This Site</h2>
          <p className="text-gray-600 mt-1">
            {salesOrders.length} {salesOrders.length === 1 ? 'order' : 'orders'} for {siteName}
          </p>
        </div>
        <button
          onClick={() => navigate(`/sales-orders/new?customer_id=${customerId}&site_id=${siteId}`)}
          className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition"
        >
          ➕ Create Sales Order
        </button>
      </div>

      {/* Sales Orders List */}
      {salesOrders.length === 0 ? (
        <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <div className="text-6xl mb-4">🛒</div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Sales Orders for This Site</h3>
          <p className="text-gray-600 mb-4">
            Create the first sales order for {siteName}
          </p>
          <button
            onClick={() => navigate(`/sales-orders/new?customer_id=${customerId}&site_id=${siteId}`)}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition"
          >
            ➕ Create First Sales Order
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-300 border-b border-gray-400">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('order_number')}>
                  SO #{getSortIndicator('order_number')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('order_date')}>
                  Order Date{getSortIndicator('order_date')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('required_date')}>
                  Required Date{getSortIndicator('required_date')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('status')}>
                  Status{getSortIndicator('status')}
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('total_amount')}>
                  Total{getSortIndicator('total_amount')}
                </th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedSOs.map((so, index) => (
                <tr 
                  key={so.id}
                  className={`hover:bg-blue-50 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-50' : 'bg-white'}`}
                  onClick={() => navigate(`/sales-orders/${so.id}`)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {so.order_number || `SO${so.id}`}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {formatDate(so.order_date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {formatDate(so.required_date)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(so.status)}`}>
                      {so.status || 'draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                    {formatCurrency(so.total_amount)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/sales-orders/${so.id}`);
                      }}
                      className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black rounded text-xs font-medium transition"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SiteSalesOrdersTab;
